import * as React from 'react';
import { filter, find, flow, forEach, get, head, map, some } from 'lodash';
import { Field, formValueSelector, InjectedFormProps, reduxForm } from 'redux-form';
import { IMtaActions, IMtaFormNames, IMtaState, PageForm } from '../../../../../../../../services/mta/models';
import { connect } from 'react-redux';
import { IStore } from '../../../../../../../../redux/IStore';
import { bindActionCreators } from 'redux';
import { mtaActions } from '../../../../../../../../services/mta/actions';
import { actions as brandingActions } from '../../../../../../../../services/branding/reducer';
import { Button } from 'react-bootstrap';
import { FaSpinner } from 'react-icons/fa';
import { IBrandingActions } from '../../../../../../../../services/branding/models';
import { MTACardHeader } from '../Components/MTACardHeader';
import { MTAButtons } from '../../../MTAButtons';
import { push } from 'react-router-redux';
import FieldFormControl from '../../../../../../../../components/FieldFormControl';
import { isRequired } from '../../../../../../../../util/validators';
import { IProduct } from '../../../../../../../../data/api/models';

interface MTAChangeDestinationFormProps extends InjectedFormProps<any> {
  mta: IMtaState;
  formValues: any;
  selectedPolicy: any;
  product: IProduct;
  actions: {
    branding: IBrandingActions;
    mta: IMtaActions;
    push(path: string);
  };

  renderDefinition(name: string, useProvidedString?: boolean): any;
}

interface IMTARemoveOptionState {
  currentAddedOptions: ISelectedOption[],
  loading: boolean
}

interface ISelectedOption {
  answerYes: string,
  selected: boolean,
  key: string,
}

class MTARemoveOptionForm extends React.Component<MTAChangeDestinationFormProps, IMTARemoveOptionState> {

  constructor(props) {
    super(props);
    const { dispatch, change } = props;
    dispatch(change('page', PageForm.MTA_REMOVE_OPTION));
    window.scrollTo(0, 0);

    this.state = {
      currentAddedOptions: this.getSelectedOptions(),
      loading: false
    }
  }

  private getOptionName = (key) => {
    const { product } = this.props;
    const option = get(product, `metadata.options.${key}`, null)
    return option?.name || ''
  };

  private getSelectedOptions = (): ISelectedOption[] => {
    const { mta, actions } = this.props;

    const selectedPremium = actions.mta.getSelectedScheme();
    const selectedPolicyOptions = get(mta, 'selectedPolicy.metadata.options', [])
    const coverLevel = get(selectedPremium, 'scheme.cover_level');
    const allBenefits = get(selectedPremium, 'information.all_benefits', [])
    const addedOptions = [];
    forEach(selectedPolicyOptions, (value, key) => {
      if (key === 'hazardous_activities') {
        return;
      }
      if (value) {
        const benefit = filter(allBenefits, ['option', key])
        addedOptions.push(this.renderOptionsObject(key, coverLevel, benefit, allBenefits, get(selectedPremium, 'information.standard_excess')));
      }
    });

    return addedOptions
  }

  public handleSubmit = () => {
    const { actions, change } = this.props;
    const { currentAddedOptions } = this.state;

    const promises = [];

    const selectedToRemove = map(currentAddedOptions, (option, key) => {
      if (option.selected === true) {
        return option.key
      }
    }).filter((selection) => !!selection)

    forEach(selectedToRemove, (option) => {
      promises.push(change(`data.attributes.metadata.options.${option}`, false));
    })

    promises.push(change('removeOption', selectedToRemove))

    this.setState({ loading: true })

    Promise.all(promises).then(() => {
      Promise.all([
        actions.mta.calculateMtaPremiums(),
      ]).then((data) => {
        actions.mta.addEndorsements();
        actions.mta.handleMTASubmit();
      });
    })

  }

  private handleOnchange = (key, index) => {
    const { currentAddedOptions } = this.state;
    const modified = [...currentAddedOptions];
    modified[index].selected = !modified[index].selected
    this.setState({ currentAddedOptions: modified })

  }

  private renderOptionsObject = (key, coverLevel, benefit, allBenefits, excess) => {

    let selected = null;
    let component = null;
    let coverLevelValue = null;

    switch (key) {
      case 'winter_sports':
        coverLevelValue = get(find(get(head(benefit), 'sub_benefits'), ['name', 'Ski equipment  Lost, damaged, stolen']), `cover_level.${coverLevel}`)

        component = (
          <>
            <div><b>Winter Sports</b></div>
            <p>{coverLevelValue} for loss, theft or damage of winter sports equipment and other specified winter sports
              related
              incidents. Covers winter sports trips lasting up to 21 days per policy term.</p>
            <p><b>If you remove this cover and intend to go on a winter sports trip, you will not be covered by the
              policy.</b></p>
          </>

        )

        selected = {
          answerYes: component,
          selected: false,
          key: 'winter_sports'
        }
        break;
      case 'gadget_cover':
        const coverLevelGadget = get(head(benefit), `cover_level.${coverLevel}`)
        const coverLevelSingleItemLimit = get(find(get(head(benefit), 'sub_benefits'), ['name', 'Single item limit']), `cover_level.${coverLevel}`)

        const personalBelongings = find(allBenefits, ['name', 'Personal belongings'])
        const coverLevelValuePerPerson = get(find(get(personalBelongings, 'sub_benefits'), ['name', 'single item limit']), `cover_level.${coverLevel}`)

        component = (
          <>
            <div><b>Gadget Cover</b></div>
            <p>Gadget cover would provide cover up to {coverLevelGadget} in total per trip for loss, theft or damage of
              a gadget owned by yourself, up to {coverLevelSingleItemLimit} per single gadget.</p>
            <p>If you remove this cover, you will only be covered up to the total valuables limit
              of {coverLevelValuePerPerson}.</p>
          </>

        )

        selected = {
          answerYes: component,
          selected: false,
          key: 'gadget_cover'
        }
        break;
      case 'cruise_cover':

        coverLevelValue = get(find(get(head(benefit), 'sub_benefits'), ['name', 'Missed port departure (travel & accommodation)']), `cover_level.${coverLevel}`)

        component = (
          <>
            <div><b>Cruise Cover</b></div>
            <p>{coverLevelValue} for specified cruise related incidents (such as travel costs to re-join the ship if
              hospitalised). We consider a cruise to be a trip lasting more than 1 day, where transport and
              accommodation are primarily on an ocean-going passenger ship.</p>
            <p><b>If you remove this cover and intend to go on a sea cruise, you will not be covered by the policy.</b>
            </p>
          </>

        )

        selected = {
          answerYes: component,
          selected: false,
          key: 'cruise_cover'
        }
        break;
      case 'excess_waiver':

        component = (
          <>
            <div><b>Excess Waiver</b></div>
            <p>Your excess in the event of a claim is {excess} per person, per section, per claim. With our optional
              excess waiver cover, you wouldn’t have to pay an excess in the event of a claim.</p>
            <p>If you remove this cover, you will have to pay an excess of {excess} per person, per section, per claim
              in the event of a claim.</p>
          </>
        )

        selected = {
          answerYes: component,
          selected: false,
          key: 'excess_waiver'
        }
        break;
      case 'enhanced_trip_disruption':

        component = (
          <>
            <div><b>Enhanced Trip Disruption Cover</b></div>
            <p>If you remove this cover you will not be covered for:</p>
            <ul>
              <li>Cover for the delay benefit with the minimum delay period to qualify for cover reduced from 12 hours to 6 hours</li>
              <li>Cover for onward expenses incurred as the result of a delay, such as additional transfer/car hire costs as well as cover for expenses for unused excursions</li>
              <li>Cover for disruption caused by airspace closure, air traffic control restrictions, mid-air emergencies, and technical issues</li>
              <li>Cover for connecting flights (outbound and return journeys only)</li>
            </ul>
          </>
        )

        selected = {
          answerYes: component,
          selected: false,
          key: 'enhanced_trip_disruption'
        }
        break;
    }

    return selected;
  }

  private hasYesAnswer = () => {
    const { currentAddedOptions } = this.state;

    return some(currentAddedOptions, ['selected', true])
  }

  private handleBackButton = () => {
    const { actions } = this.props;
    actions.push(`/account/summary`)
  }

  render() {

    const { mta } = this.props;
    const { currentAddedOptions } = this.state

    const isLoading = mta.isLoading;
    const submitButton = (
      <Button
        bsStyle="primary"
        type="button"
        onClick={this.handleSubmit}
        className="medium-width pull-right secondary-button"
        disabled={isLoading || !this.hasYesAnswer()}>
        {isLoading && (
          <FaSpinner className="fa-spin"/>
        )}
        CONTINUE
      </Button>
    );

    const backButton = (
      <Button
        id="back"
        bsStyle="primary"
        type="button"
        className="secondary-button pull-left medium-width"
        onClick={this.handleBackButton}
      >GO BACK</Button>
    )

    return (
      <>
        <div className="admiral-card" style={{ margin: '30px 0 30px 0', paddingBottom: '30px' }}>
          <MTACardHeader text={'Please confirm the option(s) to remove.'} icon={'document-adjustment'}/>

          <div className="option-selection-container">
            {currentAddedOptions.map((option: ISelectedOption, index) => {

              let selectionClass = `trip-type-btn ${index}`

              if (option?.selected) {
                selectionClass += ' selected'
              }

              return (
                <>
                  <div
                    className={option?.selected ? 'option-title-selection selected' : 'option-title-selection'}
                    key={index}>
                    <Field
                      id={index + '-btn'}
                      type="radio"
                      value={option?.selected}
                      onChange={this.handleOnchange.bind(this, option, index)}
                      component={FieldFormControl}
                      validate={[isRequired]}
                    >
                      <div className={selectionClass}>
                        <div>
                          {this.getOptionName(option?.key)}
                        </div>
                      </div>
                    </Field>
                  </div>
                </>
              )
            })}
          </div>

          <div className={'option-selected-text-container'}>
            {currentAddedOptions.map((option: ISelectedOption, index) => {
              if (option?.selected) {
                return (
                  <div>{option.answerYes}</div>
                )
              }
            })}
          </div>


        </div>
        <div className={'mt-30'}/>
        <MTAButtons
          button1={backButton}
          button2={submitButton}
        />
      </>
    )
  }
}

let selector;

export default flow([
  reduxForm({
    form: IMtaFormNames.COVER_DETAILS,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector(IMtaFormNames.COVER_DETAILS);
    return component;
  },
  connect(
    (state: IStore) => {
      const values = selector(
        state,
        'data.attributes',
      );
      return {
        mta: state.mta,
        selectedPolicy: state.mta.selectedPolicy,
        formValues: values,
        product: state?.branding?.channel?.products[0] || null
      };
    },
    (dispatch) => ({
      actions: {
        mta: bindActionCreators({ ...mtaActions }, dispatch),
        branding: bindActionCreators({ ...brandingActions }, dispatch),
        push: bindActionCreators(push, dispatch),
      },
    }),
  ),
])(MTARemoveOptionForm);
